import { GCInstant as platform } from 'src/lib/gcinstant';
import { isInLineMiniApp, Product } from '@play-co/gcinstant';
import { realMoneyPriceToReadableString } from 'src/lib/iap/iapConversion';
import { captureGenericError } from 'src/lib/sentry';
import { ProductId, webIAPPrices } from 'src/replicant/iap/iap.ruleset';
import { shopBundlesAvailableProducts } from 'src/replicant/match2/shopbundles/match2-shopbundles.ruleset';
import { getWebIAPCatalog } from 'src/lib/utils';
import {
  getProductCatalog,
  isLineIAPAvailable,
} from 'src/game/iap/component/iap.api';

/**
 * Get the catalog of shop bundles products available for web purchase, in GCInstant format
 */
export function shopBundlesGetWebIAPCatalog(): Product[] {
  const availableProducts = shopBundlesAvailableProducts;
  const catalog: Product[] = availableProducts.map((id) => {
    const priceJPY = webIAPPrices[id];
    return {
      id,
      price: String(priceJPY),
      currencyCode: 'JPY',
      // The following aren't used, but the GCInstant Product type requires them
      title: id,
      description: id,
      imageURI: id,
    };
  });

  return catalog;
}

/**
 * Get the price string for a product id
 */
export function shopBundlesGetProductPriceStr(id: ProductId) {
  // LINE Mini App uses a different catalog
  if (isLineIAPAvailable()) {
    const lineProducts = getProductCatalog('line');

    // Find the catalog item corresponding to the product id
    const catalogItem = lineProducts.find((p) => p.id === id);

    // If no catalog item found, something has gone wrong
    if (!catalogItem) {
      // Report the error but don't throw it - let the UI handle the problem gracefully
      captureGenericError('ShopBundlesError', `No catalog item for ${id}`);
      return '';
    }

    return catalogItem.priceAsString;
  }

  // If platform catalog is empty, the shop must be in 'fake' mode,
  // so use the web catalog instead, like in fake gems shop
  const catalog = platform.catalog.length
    ? platform.catalog
    : shopBundlesGetWebIAPCatalog();

  // Find the catalog item corresponding to the product id
  const catalogItem = catalog.find((p) => p.id === id);

  // If no catalog item found, something has gone wrong
  if (!catalogItem) {
    // Report the error but don't throw it - let the UI handle the problem gracefully
    captureGenericError('ShopBundlesError', `No catalog item for ${id}`);
    return '';
  }

  return realMoneyPriceToReadableString(
    catalogItem.price,
    catalogItem.currencyCode,
  );
}

/**
 * Set to true to enable purchase simulation.
 * Effective only in dev mode and if enableIAP dev setting is set.
 */
let purchaseSimulation = false;

/**
 * Enable purchase simulation
 */
export function shopBundlesEnablePurchaseSimulation(value: boolean) {
  // Ensure products are in the catalog when enabling purchase simulation
  if (value && !platform.catalog.length) {
    platform.catalog = getWebIAPCatalog();
  }

  purchaseSimulation = value;
}

/**
 * Check if purchase simulation is enabled
 */
export function shopBundlesIsPurchaseSimulationEnabled() {
  return purchaseSimulation;
}
